import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Questionnaire from '../components/Questionnaire';
import ThankYouModal from '../components/ThankYouModal';
import { useQuestionnaireContext } from '../context/QuestionnaireContext';

// ANKIETA 2 - Post-questionnaire configuration
const postQuestionnaireQuestions = [
    // Pytanie 1
    {
        id: 'app_start_ease',
        type: 'radio',
        question: 'Jak oceniasz rozpoczęcie korzystania z aplikacji:',
        required: true,
        options: [
            { value: 'very_easy', label: 'Bardzo łatwe' },
            { value: 'easy', label: 'Łatwe' },
            { value: 'neutral', label: 'Ani łatwe ani trudne' },
            { value: 'difficult', label: 'Trudne - chętnie skorzystał/abym z pomocy' },
            { value: 'very_difficult', label: 'Bardzo trudne - chętnie skorzystał/abym z pomocy' }
        ]
    },
    // Pytanie 2
    {
        id: 'app_usage_ease',
        type: 'radio',
        question: 'Jak oceniasz użytkowanie aplikacji:',
        required: true,
        options: [
            { value: 'very_easy', label: 'Bardzo łatwe' },
            { value: 'easy', label: 'Łatwe' },
            { value: 'neutral', label: 'Ani łatwe ani trudne' },
            { value: 'difficult', label: 'Trudne - chętnie skorzystał/abym z pomocy' },
            { value: 'very_difficult', label: 'Bardzo trudne - chętnie skorzystał/abym z pomocy' }
        ]
    },
    // Pytanie 3
    {
        id: 'interested_reuse',
        type: 'multiselect',
        question: 'Czy jesteś zainteresowany ponownym skorzystaniem z aplikacji?',
        required: true,
        options: [
            { value: 'other_modules', label: 'Tak, chętnie z innych modułów (np. Trudności w życiu zawodowym, Relacje, Zmiana nawyków, Rozwój osobisty, Zarządzanie emocjami i nastrojem, Zarządzanie finansami i stresem finansowym, Rodzicielstwo i opieka)' },
            { value: 'same_module', label: 'Tak, chętnie z tego samego modułu' },
            { value: 'not_interested', label: 'Nie, nie jestem zainteresowany/a' }
        ]
    },
    // Pytanie 4 (conditional - hidden if not_interested)
    {
        id: 'monthly_payment',
        type: 'text',
        question: 'Biorąc pod uwagę koszt tradycyjnej wizyty u specjalisty (powyżej 200 PLN/h) ile maksymalnie jesteś skłonny wydać na miesięczny dostęp do aplikacji wspierającej dobrostan?',
        placeholder: 'Podaj kwotę w PLN',
        required: true,
        conditional: {
            dependsOn: 'interested_reuse',
            hideWhen: 'not_interested'
        }
    },
    // Pytanie 5a - Zgoda RODO (conditional - shown before email)
    {
        id: 'gdpr_consent',
        type: 'consent',
        question: 'Zgoda na przetwarzanie danych osobowych',
        required: true,
        conditional: {
            dependsOn: 'interested_reuse',
            hideWhen: 'not_interested'
        },
        consentText: 'Akceptuję Klauzulę informacyjną i Politykę prywatności oraz wyrażam zgodę na przetwarzanie moich danych osobowych.',
        links: [
            { text: 'Klauzulę informacyjną', url: '/assets/KLAUZULA INFORMACYJNA GS.pdf' },
            { text: 'Politykę prywatności', url: '/assets/Polityka prywatności.pdf' }
        ]
    },
    // Pytanie 5b - Zgoda marketingowa (conditional)
    {
        id: 'marketing_consent',
        type: 'consent',
        question: 'Zgoda na kontakt marketingowy',
        required: false,
        conditional: {
            dependsOn: 'gdpr_consent',
            showWhen: true
        },
        consentText: 'Wyrażam zgodę na kontakt w celach marketingowych oraz otrzymywanie informacji handlowych drogą elektroniczną.',
        links: [
            { text: 'kontakt w celach marketingowych', url: '/assets/Zgoda na marketing bezpośredni i przesyłanie informacji handlowych GS.pdf' },
            { text: 'otrzymywanie informacji handlowych', url: '/assets/Zgoda na otrzymanie oferty handlowej opracowana w oparciu o Prawo Komunikacji Elektronicznej  wraz z klauzulą informacyjną GS.pdf' }
        ]
    },
    // Pytanie 5c (conditional - shown only when marketing_consent is selected)
    {
        id: 'email_for_final_version',
        type: 'text',
        question: 'Jeśli jesteś zainteresowany przetestowaniem finalnej wersji aplikacji, proszę o pozostawienie kontaktu email:',
        placeholder: 'Twój adres e-mail',
        required: false,
        conditional: {
            dependsOn: 'marketing_consent',
            showWhen: true
        }
    },
    // Pytanie 6
    {
        id: 'other_thoughts',
        type: 'textarea',
        question: 'Jakie masz inne przemyślenia/sugestie odnośnie naszego rozwiązania, którymi możesz się z nami podzielić?',
        placeholder: 'Podziel się swoimi przemyśleniami...',
        required: false
    }
];

const PostQuestionnairePage = () => {
    const navigate = useNavigate();
    const { completePostQuestionnaire } = useQuestionnaireContext();
    const [showThankYou, setShowThankYou] = useState(false);

    const handleQuestionnaireComplete = (answers) => {
        console.log('Post-questionnaire completed:', answers);
        completePostQuestionnaire(answers);
        setShowThankYou(true);
    };

    const handleBack = () => {
        navigate('/zmiana-pracy');
    };

    const handleGoHome = () => {
        setShowThankYou(false);
        navigate('/zmiana-pracy'); // Return to app where user can download PDF
    };

    return (
        <>
            <Questionnaire
                title="ANKIETA 2"
                description="Bardzo dziękujemy za próbne skorzystanie z naszej aplikacji! Naszym celem jest stworzenie narzędzia które wspiera użytkownika w osiąganiu jego celów i jest atrakcyjną cenowo alternatywą dla spotkania ze specjalistą. Będziemy bardzo wdzięczni za wypełnienie 2-min ankiety dotyczącej obserwacji aplikacji z której właśnie korzystałaś/eś."
                questions={postQuestionnaireQuestions}
                onComplete={handleQuestionnaireComplete}
                onBack={handleBack}
                showProgress={true}
                submitButtonText="Zakończ ankietę"
            />
            <ThankYouModal
                open={showThankYou}
                onClose={() => setShowThankYou(false)}
                onGoHome={handleGoHome}
            />
        </>
    );
};

export default PostQuestionnairePage;
